// Critical-fields contract — see aegle-docs critical_fields_contract.md
//
// This module derives its entries from that document's per-schema sections
// (1.1 Claimant / 1.2 Reimbursement / 1.3 ClaimItem). Category-B entries
// (intentionally not pickable — auto-resolved at load time) are excluded
// because reviewers cannot map carrier columns to them. Cosmetic-tier
// fields are also excluded so the UI signal stays meaningful.
//
// When the doc updates, update this file. The doc is the source of truth.

import { TargetSchema } from "@/types";

/**
 * Severity tiers, displayed to claims analysts as "Required" and
 * "Recommended". `cosmetic` exists for completeness but no entries currently
 * emit it (the map filters down to required + recommended only).
 */
export type CriticalFieldSeverity = "required" | "recommended" | "cosmetic";

/**
 * Gating condition that decides whether an advisory entry is relevant for the
 * current document. When omitted, the entry is unconditional (always relevant).
 *
 *  - `family-deductible-policy`: fires only when the policy has a family
 *    deductible (i.e. the family-unit grouping that needs subscriber-id).
 *  - `extracted-record-has-data`: fires only when at least one extracted
 *    record of the named schema has a non-empty value for `field`. Used for
 *    status-conditional required fields (e.g. denial_reason matters only when
 *    at least one record has a denial_amount).
 */
export type AdvisoryCondition =
  | { type: "family-deductible-policy" }
  | { type: "extracted-record-has-data"; schema: TargetSchema; field: string };

/**
 * How an alternative entry combines with its slot's primary.
 *  - `alternative`: the entry alone satisfies the slot. Reserved for cases
 *    where two distinct fields are interchangeable for the same purpose
 *    (no entries use this today, but the model leaves room — e.g. an
 *    either-of carrier-id pair).
 *  - `components`: every entry tagged `components` for the same primary must
 *    be mapped together to satisfy the slot (e.g. `claimant_first_name` AND
 *    `claimant_last_name` compose a full name).
 *
 * The primary itself is always a singular satisfier — mapping it directly
 * satisfies the slot regardless of `components` membership.
 */
export type CompositeKind = "alternative" | "components";

export interface CriticalFieldEntry {
  /** target_field name as it appears in extraction templates */
  field: string;
  severity: CriticalFieldSeverity;
  /** Consumer category labels matching the by-consumer index in the contract doc. */
  consumers: string[];
  /** One-sentence "what breaks when this isn't mapped" pulled from the doc. */
  impact: string;
  /**
   * When set, this entry pairs with another entry's slot — either as a
   * standalone alternative (default) or as a piece of a multi-part composite.
   * Used by coverage counters so they don't double-charge a template that
   * maps via either route. Pair with `compositeKind` to distinguish.
   */
  compositeAlternativeFor?: string;
  /**
   * Combination semantics for `compositeAlternativeFor`. Defaults to
   * `"alternative"` (the entry alone satisfies the slot). Set to
   * `"components"` when the slot requires all sibling components mapped
   * together.
   */
  compositeKind?: CompositeKind;
  /**
   * Optional gating condition. When present, the advisory layer suppresses
   * the entry until the condition holds against the current document's
   * extracted records or projection context. Coverage counters and the
   * dropdown markers ignore this — those surfaces are template-level, not
   * document-level.
   */
  condition?: AdvisoryCondition;
}

export const CRITICAL_FIELDS: Record<TargetSchema, CriticalFieldEntry[]> = {
  [TargetSchema.CLAIMANT]: [
    {
      field: "claimant_name",
      severity: "required",
      consumers: ["Claimant identification", "Document-review projection", "List/show display", "Claims summary"],
      impact:
        "Without a name, the system can't identify the claimant. Records with no name are dropped when you approve the document.",
    },
    {
      field: "claimant_first_name",
      severity: "required",
      consumers: ["Used to build claimant_name"],
      impact:
        "If the document doesn't have a single full-name column, map first and last name separately — the system combines them into the full name.",
      compositeAlternativeFor: "claimant_name",
      compositeKind: "components",
    },
    {
      field: "claimant_last_name",
      severity: "required",
      consumers: ["Used to build claimant_name"],
      impact:
        "If the document doesn't have a single full-name column, map first and last name separately — the system combines them into the full name.",
      compositeAlternativeFor: "claimant_name",
      compositeKind: "components",
    },
    {
      field: "member_id",
      severity: "recommended",
      consumers: ["Matching existing claimants", "List/show display"],
      impact:
        "Member ID is how the system recognizes a claimant on future uploads. Without it, every upload creates a new claimant record, leading to duplicates.",
    },
    {
      field: "claimant_birth_date",
      severity: "recommended",
      consumers: ["Matching dependents to subscribers", "Claimant detail page"],
      impact:
        "Birth date helps match dependents to the right subscriber. Without it, matching falls back to name only, which is less reliable.",
    },
    {
      field: "claimant_relationship",
      severity: "recommended",
      consumers: ["Family-deductible routing", "List/show display", "Family column on the review grid"],
      impact:
        "Relationship tells the system whether a claimant is Subscriber, Spouse, or Dependent. Without it, the Family column on the review grid won't show a clear role.",
    },
    {
      field: "claimant_related_subscriber_id",
      severity: "recommended",
      consumers: ["Family-unit grouping", "Family column on the review grid"],
      impact:
        "On family-deductible policies, this links dependents to their subscriber. Without it, dependents won't pair to a subscriber name in the Family column.",
      condition: { type: "family-deductible-policy" },
    },
    {
      field: "ytd_total_paid",
      severity: "recommended",
      consumers: ["Total YTD column on the claims summary", "Watch list % of deductible"],
      impact:
        "If the carrier provides a year-to-date total column, mapping it preserves the carrier's authoritative number. Otherwise the system recomputes from claim items, which can double-count when documents overlap in date range. Skip if no YTD column exists.",
    },
  ],

  [TargetSchema.REIMBURSEMENT]: [
    {
      field: "requested_amount",
      severity: "required",
      consumers: ["Required field", "Total Requested column", "Detail page header"],
      impact: "Required to load the record. The Total Requested column on the claims summary uses this.",
    },
    {
      field: "reimbursement_amount",
      severity: "recommended",
      consumers: ["Total Reimbursed column", "Variance calculation", "List view"],
      impact:
        "The Total Reimbursed column and the Variance calculation use this. Required for paid records — non-paid records load without it, but totals will under-report.",
    },
    {
      field: "pended_amount",
      severity: "recommended",
      consumers: ["Total Pended column", "List view"],
      impact:
        "The Total Pended column on the claims summary uses this. Pended + denied + reimbursed cannot exceed the requested amount.",
    },
    {
      field: "denial_amount",
      severity: "recommended",
      consumers: ["Total Denied column", "Variance calculation", "List view"],
      impact: "The Total Denied column and the Variance calculation use this.",
    },
    {
      field: "adjustment_amount",
      severity: "recommended",
      consumers: ["Adjustments column on the claims summary", "Document-review row chips"],
      impact:
        "Adjustments are treated as deductions, even when the carrier shows them as positive numbers (the system flips the sign on load). The Adjustments column on the claims summary uses this.",
    },
    {
      field: "service_start_date",
      severity: "recommended",
      consumers: ["Duplicate detection on re-upload", "Date-range validation"],
      impact:
        "The system uses service-start date together with the carrier's payment reference to recognize duplicates on re-upload. Without it (and without received date or payment reference), each re-upload creates a duplicate.",
    },
    {
      field: "received_date",
      severity: "recommended",
      consumers: ["Duplicate detection on re-upload"],
      impact: "Used alongside service-start date to recognize duplicates on re-upload.",
    },
    {
      field: "payment_date",
      severity: "recommended",
      consumers: ["Claims summary date-range filter", "List view"],
      impact:
        "The claims summary filters reimbursements by payment date. Without it, the reimbursement is excluded from period totals and the Reimbursed / Pended / Denied columns under-report. Carriers vary in label — Check Date, Payment Date, or ACH Date all map here.",
    },
    {
      field: "payment_reference",
      severity: "recommended",
      consumers: ["Duplicate detection on re-upload", "List view"],
      impact:
        "The carrier's payment-tracking number — Check #, ACH transaction number, or EFT trace number all map here. Used to recognize duplicates on re-upload; without it, every re-upload creates duplicates.",
    },
    {
      field: "provider",
      severity: "recommended",
      consumers: ["Duplicate detection on re-upload"],
      impact:
        "Part of the duplicate-detection key. Without it, the system may treat reimbursements from different providers as the same record on re-upload.",
    },
    {
      field: "pending_reason",
      severity: "required",
      consumers: ["Required for pended reimbursements", "Detail page"],
      impact: "Required for pended reimbursements. Without it, pended records won't load.",
      condition: { type: "extracted-record-has-data", schema: TargetSchema.REIMBURSEMENT, field: "pended_amount" },
    },
    {
      field: "denial_reason",
      severity: "required",
      consumers: ["Required for denied reimbursements", "Detail page"],
      impact: "Required for denied reimbursements. Without it, denied records won't load.",
      condition: { type: "extracted-record-has-data", schema: TargetSchema.REIMBURSEMENT, field: "denial_amount" },
    },
    {
      field: "adjustment_reason",
      severity: "required",
      consumers: ["Required when an adjustment is recorded", "Detail page"],
      impact: "Required when a reimbursement has an adjustment. Without it, those records won't load.",
      condition: { type: "extracted-record-has-data", schema: TargetSchema.REIMBURSEMENT, field: "adjustment_amount" },
    },
  ],

  [TargetSchema.CLAIM_ITEM]: [
    // claim_type and claim_status are auto-resolved at load time (Category B
    // in the contract doc). claim_type derives from the document's medical-vs-
    // pharmacy stoploss-claims classification; claim_status defaults to
    // "submitted" when the carrier doesn't emit a status column. Reviewers
    // shouldn't see advisories asking them to map either — the loader handles
    // both. Carriers that DO emit explicit columns still override the
    // defaults.
    {
      field: "source_claim_id",
      severity: "recommended",
      consumers: ["Duplicate detection on re-upload", "Display"],
      impact:
        "The carrier's claim ID. Lets the system recognize duplicate claim lines on re-upload — without it, re-uploads create duplicates. Map if your document includes a claim-line reference.",
    },
    {
      field: "primary_diagnosis",
      severity: "recommended",
      consumers: ["Medical / RX analytics", "Display"],
      impact:
        "ICD diagnosis codes drive medical/RX analytics and diagnosis-level reporting. Records load without them, but line detail is reduced. Map if your document includes diagnosis codes.",
    },
    {
      field: "procedure_code",
      severity: "recommended",
      consumers: ["Line-level analytics", "Display"],
      impact:
        "CPT codes drive line-level analytics on the claims summary. Records load without them, but line detail is reduced. Map if your document includes procedure codes.",
    },
    {
      field: "paid_amount",
      severity: "recommended",
      consumers: ["Claims summary Medical / RX totals", "Watch-list claimant spend"],
      impact:
        "The Medical / RX totals on the claims summary and the claimant-spend total on the watch list sum this. Without it, both totals under-report.",
    },
    {
      field: "paid_date",
      severity: "recommended",
      consumers: ["Claims summary date filter", "Watch-list date filter"],
      impact:
        "The claims summary and watch list filter claim items by paid date. Without it, the claim is excluded from period totals.",
    },
  ],

  // StoplossClaim, Policy, Client are not extraction-mappable today
  // (empty SCHEMA_FIELDS). Entries empty for completeness.
  [TargetSchema.STOPLOSS_CLAIM]: [],
  [TargetSchema.POLICY]: [],
  [TargetSchema.CLIENT]: [],
};

/**
 * Returns the critical-field entries for a schema. Empty array for schemas
 * that aren't extraction-mappable.
 */
export const getCriticalFields = (schema: TargetSchema): CriticalFieldEntry[] => CRITICAL_FIELDS[schema] ?? [];

/**
 * True iff the field is critical for the given schema.
 */
export const isCriticalField = (schema: TargetSchema, field: string): boolean =>
  getCriticalFields(schema).some((entry) => entry.field === field);

/**
 * Returns the entry for a given (schema, field) pair, or undefined.
 */
export const getCriticalFieldEntry = (schema: TargetSchema, field: string): CriticalFieldEntry | undefined =>
  getCriticalFields(schema).find((entry) => entry.field === field);

/**
 * Coverage counter — given a schema and the set of mapped target fields,
 * returns how many distinct critical "slots" are mapped vs. expected.
 *
 * A slot is satisfied when ANY of these routes resolves:
 *  - The primary field is mapped directly
 *    (e.g. `claimant_name` → satisfies the claimant-name slot).
 *  - Any sibling tagged `compositeKind: "alternative"` is mapped (no entries
 *    use this today; reserved for either-of pairs the contract may add).
 *  - ALL siblings tagged `compositeKind: "components"` are mapped together
 *    (e.g. `claimant_first_name` AND `claimant_last_name` → compose into a
 *    full name. Mapping just one component does NOT satisfy the slot —
 *    the loader can't form a name without both halves.)
 */
export const getCriticalCoverage = (
  schema: TargetSchema,
  mappedFields: Iterable<string>
): { mapped: number; total: number; unmappedSlots: CriticalFieldEntry[] } => {
  const entries = getCriticalFields(schema);
  const mappedSet = new Set(mappedFields);

  // Group entries into slots: each non-composite entry is its own slot;
  // entries with compositeAlternativeFor attach to that primary slot.
  const slotPrimaries = entries.filter((e) => !e.compositeAlternativeFor);
  const slots = slotPrimaries.map((primary) => {
    const siblings = entries.filter((e) => e.compositeAlternativeFor === primary.field);
    const components = siblings.filter((e) => e.compositeKind === "components");
    const alternatives = siblings.filter((e) => (e.compositeKind ?? "alternative") === "alternative");
    return { primary, components, alternatives };
  });

  let mapped = 0;
  const unmappedSlots: CriticalFieldEntry[] = [];
  for (const slot of slots) {
    const primaryMapped = mappedSet.has(slot.primary.field);
    const anyAlternativeMapped = slot.alternatives.some((m) => mappedSet.has(m.field));
    // Components are satisfied only when ALL of them are mapped together —
    // a half-mapped composite (e.g. last name without first) does not form
    // the primary value and so doesn't satisfy the slot.
    const allComponentsMapped = slot.components.length > 0 && slot.components.every((m) => mappedSet.has(m.field));

    if (primaryMapped || anyAlternativeMapped || allComponentsMapped) {
      mapped++;
    } else {
      unmappedSlots.push(slot.primary);
    }
  }

  return { mapped, total: slots.length, unmappedSlots };
};
